import React, { useState, useEffect } from 'react';
import { css } from '@emotion/core';
import { Menu, MenuItem, IconButton, Tooltip } from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { Observer } from 'mobx-react';
import InvalidIcon from '@material-ui/icons/Warning';
import { Link } from 'react-router-dom';
import { useRootStore } from '../../../../stores/BridgeProvider';
import { FlexDiv } from '../../../styles/styles';
import Cell from '../../common/Cell';
import VoucherTransaction from './VoucherTransaction';
import VoucherModal from './VoucherModal';
import RenderPropModal from '../../common/RenderPropModal';
import NewVoucherTransactionModal from './NewVoucherTransactionModal';
import {
  VoucherTypeIdEnumsShort,
  VoucherStatusEnumsShort,
  VoucherStatusEnums,
} from '../../../common/Enums';
import { COLORS } from '../../../util/Colors';
import { VoucherSchema } from '../../../../validation';
import { safeEval } from '../../../util/UtilFunctions';

const infoStyle = css`
  display: flex;
  justify-content: space-around;
  align-items: center;
  font-size: 0.8rem;
  span {
    flex: 1;
    &:first-of-type {
      text-align: left;
    }
    &:last-of-type {
      text-align: right;
    }
    overflow: hidden;
    text-overflow: clip;
  }
`;

const headerStyle = css`
  background: ${COLORS.blue};
  color: ${COLORS.white};
  height: 55px;
  padding: 5px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  h4 {
    text-align: left;
    margin: 0px !important;
    margin-bottom: 4px !important;
  }
`;

const headerInfoBlock = css`
  max-width: 310px;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  overflow: hidden;
  text-overflow: clip;
  // display: flex;
  flex: 1;
  // justify-content: space-between;
  margin-right: 2px;
`;

const VoucherCell = ({ original }) => (
  <Observer
    render={() => {
      const { dealMapperStore, dataStore } = useRootStore();
      const [validationErrors, setValidationErrors] = useState(null);
      const [anchorEl, setAnchorEl] = React.useState(null);

      const handleMenuClick = event => {
        setAnchorEl(event.currentTarget);
      };

      const handleMenuClose = () => {
        setAnchorEl(null);
      };

      const {
        VoucherNumber,
        VoucherId,
        DueDate,
        ContactName,
        ContactId,
        VoucherTypeDesc,
        voucherTransactions,
        Seed,
        StatusId,
        VoucherTypeId,
        Pending,
      } = original;

      const notMappable = StatusId > 4;

      useEffect(() => {
        let unmounted = false;
        const checkValidity = async () => {
          try {
            await VoucherSchema.validate(original, {
              abortEarly: false,
            });
            if (validationErrors) setValidationErrors(null);
          } catch (e) {
            if (!unmounted) {
              setValidationErrors(e.errors);
            }
          }
        };

        checkValidity();
        return () => {
          unmounted = true;
        };
      }, [original]);

      const ContactDisplay =
        ContactName ||
        safeEval(() => dataStore.getContactFromId(ContactId).Name, 'N/A');
      return (
        <RenderPropModal
          modalStyle={{ width: '600px' }}
          cardDisplay={({ handleClose }) => (
            <FlexDiv style={{ width: '100%' }}>
              <VoucherModal
                voucherId={original.VoucherId}
                handleClose={handleClose}
              />
            </FlexDiv>
          )}
          enterHover={() => {
            dealMapperStore.dealMapperViewStore.setHoveredElement(original);
          }}
          exitHover={() => {
            dealMapperStore.dealMapperViewStore.setHoveredElement(null);
          }}
          manageHandleOpen
        >
          {() => (
            <Cell cellObject={original}>
              <div css={headerStyle}>
                {validationErrors && (
                  <Tooltip
                    title={
                      <ul style={{ padding: 0, listStyleType: 'none' }}>
                        {validationErrors.map(error => (
                          <li key={error}>* {error}</li>
                        ))}
                      </ul>
                    }
                  >
                    <InvalidIcon
                      style={{
                        color: COLORS.red,
                        marginRight: '8px',
                      }}
                    />
                  </Tooltip>
                )}
                <div css={headerInfoBlock}>
                  <h4>{VoucherNumber}</h4>
                  <div css={infoStyle}>
                    <span>{ContactDisplay}</span>
                    <span>{VoucherTypeIdEnumsShort[VoucherTypeId]}</span>
                    {notMappable ? (
                      <Tooltip
                        title={`${VoucherStatusEnums[StatusId]} vouchers will not have their transactions mapped.`}
                        placement="top"
                        arrow
                      >
                        <span
                          style={{
                            color: COLORS.red,
                          }}
                        >
                          {VoucherStatusEnumsShort[StatusId]}
                        </span>
                      </Tooltip>
                    ) : (
                      <span>{VoucherStatusEnumsShort[StatusId]}</span>
                    )}
                    <span>{DueDate}</span>
                  </div>
                </div>
                <div onClick={e => e.stopPropagation()}>
                  <IconButton
                    style={{ color: 'white' }}
                    onClick={handleMenuClick}
                  >
                    <MoreVertIcon />
                  </IconButton>
                  <RenderPropModal
                    cardDisplay={({ handleClose }) => (
                      <NewVoucherTransactionModal
                        voucher={original}
                        handleClose={handleClose}
                      />
                    )}
                  >
                    {({ handleOpen }) => (
                      <Menu
                        anchorEl={anchorEl}
                        keepMounted
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                      >
                        <MenuItem
                          onClick={e => {
                            handleOpen(e);
                            handleMenuClose();
                          }}
                        >
                          New Transaction
                        </MenuItem>

                        {!Pending && (
                          <MenuItem
                            disabled={
                              !VoucherId || dealMapperStore.isAnythingPending
                            }
                            onClick={() => {
                              dealMapperStore.unloadVoucher(original);
                              handleMenuClose();
                            }}
                          >
                            Unload Voucher
                          </MenuItem>
                        )}
                        {Pending && (
                          <MenuItem
                            onClick={() => {
                              dealMapperStore.removePendingVoucher(original);
                              handleMenuClose();
                            }}
                          >
                            Remove Voucher
                          </MenuItem>
                        )}

                        {!Pending && (
                          <MenuItem
                            component={Link}
                            to={`/vouchers/${VoucherId}`}
                          >
                            Go to Voucher
                          </MenuItem>
                        )}
                      </Menu>
                    )}
                  </RenderPropModal>
                </div>
              </div>
              <div style={{ padding: '0rem 0px' }}>
                {voucherTransactions.map(it => (
                  <VoucherTransaction original={it} key={it.VoucherTranId} />
                ))}
              </div>
            </Cell>
          )}
        </RenderPropModal>
      );
    }}
  />
);

export default VoucherCell;
